import { STORAGE_KEY } from "@constants";
import { useMemberSettings } from "@hooks/useMemberSettings";
import { useUnderMaintenance } from "@hooks/useUnderMaintenance";
import { useStatsigClient } from "@statsig/react-bindings";
import { Language } from "@types";
import { getValidLanguage } from "@utils/locales";
import logger from "@utils/logger";
import { PropsWithChildren, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

interface MemberSettingsLoaderProps extends PropsWithChildren {}

/**
 * Check if member settings are loaded
 * If loaded, show app
 */
const MemberSettingsLoader = ({ children }: MemberSettingsLoaderProps) => {
  const { isLoaded, language, locales } = useMemberSettings();
  const { i18n } = useTranslation();
  const { underMaintenance } = useUnderMaintenance();
  const { getExperiment, client } = useStatsigClient();

  const isStatsigLoading = useMemo(() => {
    logger._console.log("[MemberSettingsLoader] isStatsigLoading", client?.loadingStatus);
    return client?.loadingStatus === "Loading";
  }, [client?.loadingStatus]);

  const isDisplayChildren = useMemo(() => {
    // Bypass member setting loader if under maintenance
    if (underMaintenance) return true;
    return isLoaded;
  }, [underMaintenance, isLoaded]);

  const hasSeenNotLoaded = useRef(false);

  const isFirstLoadOnThisDevice = useMemo(() => {
    if (hasSeenNotLoaded.current) return true;
    if (!isLoaded) {
      hasSeenNotLoaded.current = true;
      return true;
    }
    return false;
  }, [isLoaded]);

  // Initialize language settings once on component mount
  useEffect(() => {
    const initializeLanguage = () => {
      try {
        let currentLanguage = localStorage.getItem(STORAGE_KEY.LANGUAGE);
        let shouldChangeLanguage = false;
        const isManualLanguageChange = localStorage.getItem(STORAGE_KEY.LANGUAGE_MANUAL) === "true";

        logger._console.log("[MemberSettingsLoader] initializeLanguage", {
          currentLanguage,
          isStatsigLoading,
          isFirstLoadOnThisDevice,
          serverSettingsLoaded: isLoaded,
          isManualLanguageChange,
        });

        // For first load on a device, wait for experiment readiness
        if (isFirstLoadOnThisDevice && isStatsigLoading && !isManualLanguageChange) {
          return;
        }

        // New device (or first-load): apply experiment gate for default language
        if (isFirstLoadOnThisDevice && !isManualLanguageChange) {
          const defaultLang = getExperiment("php_default_language")?.get("default_lang", language);
          logger._console.log(
            "[MemberSettingsLoader] experiment default_lang (first-load)",
            defaultLang,
            "server language",
            language,
          );

          if (defaultLang === "device") {
            const browserLangRaw =
              (typeof navigator !== "undefined" && (navigator.languages?.[0] || navigator.language)) || "";
            const browserLang = browserLangRaw.toLowerCase();
            const browserBase = browserLang.split("-")[0];
            logger._console.log("[MemberSettingsLoader] device language requested", {
              browserLangRaw,
              browserLang,
              browserBase,
              navigatorLanguages: typeof navigator !== "undefined" ? navigator.languages : undefined,
            });
            const matchedLocale = (locales ?? []).find((l) => {
              const code = l.code?.toLowerCase() || "";
              return code === browserLang || code === browserBase || code.startsWith(`${browserBase}-`);
            });
            logger._console.log("[MemberSettingsLoader] matchedLocale", matchedLocale);
            currentLanguage = matchedLocale?.code || (language as string);
            logger._console.log("[MemberSettingsLoader] selected currentLanguage (device path)", currentLanguage);
          } else {
            logger._console.log("[MemberSettingsLoader] using experiment default_lang (non-device)", defaultLang);
            currentLanguage = defaultLang;
          }
          shouldChangeLanguage = true;
        } else if (!currentLanguage) {
          // Not first load: use server-configured language without experiment
          currentLanguage = language as string;
          shouldChangeLanguage = true;
        }

        // If current language is not in the available list,
        // try server language if it's in the list, otherwise use first available
        if (!locales?.map((locale) => locale.code)?.includes(currentLanguage)) {
          const previousLanguage = currentLanguage;
          currentLanguage = getValidLanguage(locales ?? [], language as Language) ?? "";
          logger._console.log("[MemberSettingsLoader] language not in locales, fallback applied", {
            previousLanguage,
            fallbackLanguage: currentLanguage,
          });
          shouldChangeLanguage = true;
        }

        if (shouldChangeLanguage && currentLanguage) {
          logger._console.log("[MemberSettingsLoader] changing i18n language to", currentLanguage);
          i18n?.changeLanguage(currentLanguage as Language);
        }
      } catch (error) {
        logger._console.error("Failed to initialize language:", error);
        // On error, try server language if it's in the list, otherwise first available
        const fallbackLang = getValidLanguage(locales ?? [], language as Language) ?? "";
        logger._console.log("[MemberSettingsLoader] error path fallback language", fallbackLang);
        if (fallbackLang) {
          i18n?.changeLanguage(fallbackLang as Language);
        }
      }
    };

    if (isDisplayChildren) {
      initializeLanguage();
    }
  }, [language, locales, i18n, isFirstLoadOnThisDevice, isStatsigLoading, isLoaded, getExperiment, isDisplayChildren]);

  if (!isDisplayChildren) {
    return null;
  }

  return children;
};

export default MemberSettingsLoader;
